import { useState, useEffect, useCallback } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Api from '../../Auth/Api';
import { decodeJWT } from '../../Components/jwtUtils';
import FormRegistrarPraticaMassa from '../Praticas/FormRegistrarPraticaMassa';
import TabelaSelecaoUsuariosPratica from '../Praticas/TabelaSelecaoUsuariosPratica';

const formatDate = (dateString) => {
    if (!dateString) return '';
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [ano, mes, dia] = dateString.split('-');
        return `${dia}/${mes}/${ano}`;
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
};

const buildDatetimeLocal = (dataTreinamento, horaTreinamento) => {
    if (!dataTreinamento) return '';
    const hora = horaTreinamento ? horaTreinamento.substring(0, 5) : '00:00';
    return `${dataTreinamento}T${hora}`;
};

function ModalRealizarAgendamento({ show, onHide, agendamento, onSuccess }) {
    const token = JSON.parse(localStorage.getItem('user_token'));
    const nivel = decodeJWT(token).nivel;
    const isAdmin = nivel === 'Administrador';

    const [showAlerta, setShowAlerta] = useState(false);
    const [mensagemAlerta, setMensagemAlerta] = useState('');
    const [loading, setLoading] = useState(false);
    const [cursoTipo, setCursoTipo] = useState(null);
    const [loadingUsuarios, setLoadingUsuarios] = useState(false);
    const [usuariosCurso, setUsuariosCurso] = useState([]);
    const [praticasRegistradas, setPraticasRegistradas] = useState({});
    const [registrarPratica, setRegistrarPratica] = useState(false);
    const [usuariosSelecionados, setUsuariosSelecionados] = useState([]);
    const [listaPresencaSelected, setListaPresencaSelected] = useState(false);
    const [fotosSelected, setFotosSelected] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            data_treinamento: '',
            local_treinamento: '',
            gestor: '',
        },
    });

    const resetModalState = useCallback(() => {
        setRegistrarPratica(false);
        setUsuariosSelecionados([]);
        setListaPresencaSelected(false);
        setFotosSelected(false);
        setUsuariosCurso([]);
        setPraticasRegistradas({});
        setCursoTipo(null);
        reset({
            data_treinamento: '',
            local_treinamento: '',
            gestor: '',
            lista_presenca: undefined,
            fotos: undefined,
        });
    }, [reset]);

    const carregarDadosPratica = useCallback(async (idCurso, agendamentoData) => {
        setLoadingUsuarios(true);
        try {
            const [infoResponse, usuariosResponse, praticasResponse] = await Promise.all([
                Api.get(`api/info-curso/${idCurso}`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                Api.get(`api/listar-usuarios-por-curso/${idCurso}/todos`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                Api.get(`api/cursos/${idCurso}/praticas`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            const tipo = infoResponse.data?.[0]?.tipo || null;
            setCursoTipo(tipo);
            setUsuariosCurso(usuariosResponse.data || []);

            const praticasMap = {};
            (praticasResponse.data || []).forEach((pratica) => {
                praticasMap[pratica.idUsuario] = {
                    ativa: !pratica.arquivado,
                    id: pratica.id,
                    arquivada: pratica.arquivado,
                };
            });
            setPraticasRegistradas(praticasMap);

            reset({
                data_treinamento: buildDatetimeLocal(
                    agendamentoData.data_treinamento,
                    agendamentoData.hora_treinamento
                ),
                local_treinamento: agendamentoData.id_unidade || '',
                gestor: agendamentoData.nome_instrutor || agendamentoData.nome_solicitante || '',
                lista_presenca: undefined,
                fotos: undefined,
            });
        } catch (error) {
            console.error('Erro ao carregar dados para prática em massa:', error);
            setMensagemAlerta('Erro ao carregar usuários do curso.');
            setShowAlerta(true);
        } finally {
            setLoadingUsuarios(false);
        }
    }, [token, reset]);

    useEffect(() => {
        if (!show) {
            resetModalState();
            return;
        }

        if (!agendamento) {
            return;
        }

        resetModalState();

        if (isAdmin && agendamento.id_curso) {
            carregarDadosPratica(agendamento.id_curso, agendamento);
        }
    }, [show, agendamento, isAdmin, resetModalState, carregarDadosPratica]);

    const registrarPraticaMassa = async (formData, idCurso) => {
        const payload = new FormData();
        payload.append('idUsuarios', JSON.stringify(usuariosSelecionados));
        payload.append('data_treinamento', formData.data_treinamento);
        payload.append('local_treinamento', formData.local_treinamento);
        payload.append('gestor', formData.gestor);

        if (formData.lista_presenca instanceof FileList) {
            payload.append('lista_presenca', formData.lista_presenca[0]);
        }

        if (formData.fotos instanceof FileList) {
            for (let i = 0; i < formData.fotos.length; i += 1) {
                payload.append(`fotos[${i}]`, formData.fotos[i]);
            }
        }

        await Api.post(`/api/registrar-pratica-massa/${idCurso}`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
            },
        });
    };

    const marcarComoRealizado = async () => {
        await Api.post(`/api/agendamentos-treinamentos/${agendamento.id}/realizar`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        });
    };

    const handleConfirmarSimples = async () => {
        setLoading(true);
        try {
            await marcarComoRealizado();
            onSuccess({ praticaRegistrada: false });
        } catch (error) {
            setMensagemAlerta(error.response?.data?.message || 'Erro ao marcar como realizado');
            setShowAlerta(true);
        } finally {
            setLoading(false);
        }
    };

    const onSubmitAdmin = async (formData) => {
        const deveRegistrarPratica = registrarPratica && cursoTipo === 'misto';

        if (deveRegistrarPratica && usuariosSelecionados.length === 0) {
            setMensagemAlerta('Selecione pelo menos um participante para registrar a prática.');
            setShowAlerta(true);
            return;
        }

        setLoading(true);
        let realizadoOk = false;

        try {
            await marcarComoRealizado();
            realizadoOk = true;

            if (deveRegistrarPratica && usuariosSelecionados.length > 0) {
                await registrarPraticaMassa(formData, agendamento.id_curso);
                onSuccess({
                    praticaRegistrada: true,
                    totalUsuarios: usuariosSelecionados.length,
                });
                return;
            }

            onSuccess({ praticaRegistrada: false });
        } catch (error) {
            const mensagem = error.response?.data?.message || 'Erro ao processar solicitação';

            if (realizadoOk) {
                onSuccess({
                    praticaRegistrada: false,
                    parcial: true,
                    mensagem: `Agendamento realizado, mas falha ao registrar prática: ${mensagem}`,
                });
                return;
            }

            setMensagemAlerta(mensagem);
            setShowAlerta(true);
        } finally {
            setLoading(false);
        }
    };

    if (!agendamento) return null;

    const exibirSecaoPratica = isAdmin && cursoTipo === 'misto';
    const aguardandoTipoCurso = isAdmin && show && cursoTipo === null && loadingUsuarios;
    const modalSize = isAdmin && (exibirSecaoPratica || aguardandoTipoCurso) ? 'xl' : 'lg';

    const resumoAgendamento = (
        <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
                <p className="mb-2">Deseja marcar este agendamento como realizado?</p>
                <div className="small">
                    <div><strong>Treinamento:</strong> {agendamento.nome_curso}</div>
                    <div><strong>Data:</strong> {formatDate(agendamento.data_treinamento)}</div>
                    {agendamento.hora_treinamento && (
                        <div><strong>Horário:</strong> {agendamento.hora_treinamento.substring(0, 5)}</div>
                    )}
                    {agendamento.id_unidade && (
                        <div><strong>Unidade:</strong> {agendamento.id_unidade}</div>
                    )}
                    {agendamento.nome_solicitante && (
                        <div><strong>Solicitante:</strong> {agendamento.nome_solicitante}</div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Modal size={modalSize} show={show} onHide={onHide} backdrop="static">
                {aguardandoTipoCurso ? (
                    <>
                        <Modal.Header closeButton>
                            <span className="fw-semibold fs-5 text-primary">Marcar como Realizado</span>
                        </Modal.Header>
                        <Modal.Body className="bg-gray-100 p-4 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </Modal.Body>
                    </>
                ) : isAdmin && exibirSecaoPratica ? (
                    <form onSubmit={handleSubmit(onSubmitAdmin)}>
                        <Modal.Header closeButton>
                            <span className="fw-semibold fs-5 text-primary">Marcar como Realizado</span>
                        </Modal.Header>
                        <Modal.Body className="bg-gray-100 p-4">
                            {resumoAgendamento}

                            <div className="card border-0 shadow-sm mb-3">
                                <div className="card-body">
                                    <Form.Check
                                        type="switch"
                                        id="registrar-pratica-realizado"
                                        label="Registrar prática para participantes"
                                        checked={registrarPratica}
                                        onChange={(e) => {
                                            setRegistrarPratica(e.target.checked);
                                            if (!e.target.checked) {
                                                setUsuariosSelecionados([]);
                                                setListaPresencaSelected(false);
                                                setFotosSelected(false);
                                            }
                                        }}
                                    />
                                    <small className="text-muted d-block mt-1">
                                        Opcional. Se ativado, registra prática em massa para os participantes selecionados.
                                    </small>
                                </div>
                            </div>

                            {registrarPratica && (
                                <>
                                    <div className="mb-3">
                                        <TabelaSelecaoUsuariosPratica
                                            usuarios={usuariosCurso}
                                            praticasRegistradas={praticasRegistradas}
                                            selectedIds={usuariosSelecionados}
                                            onSelectionChange={setUsuariosSelecionados}
                                            loading={loadingUsuarios}
                                        />
                                    </div>
                                    <FormRegistrarPraticaMassa
                                        register={register}
                                        control={control}
                                        errors={errors}
                                        listaPresencaSelected={listaPresencaSelected}
                                        setListaPresencaSelected={setListaPresencaSelected}
                                        fotosSelected={fotosSelected}
                                        setFotosSelected={setFotosSelected}
                                        idPrefix="realizar_agendamento"
                                    />
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer className="justify-content-end">
                            <button type="button" className="btn me-2" onClick={onHide} disabled={loading}>
                                Cancelar
                            </button>
                            {registrarPratica ? (
                                <button type="submit" className="btn btn-success" disabled={loading || usuariosSelecionados.length === 0}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Processando...
                                        </>
                                    ) : (
                                        `Confirmar e registrar prática (${usuariosSelecionados.length})`
                                    )}
                                </button>
                            ) : (
                                <button type="button" className="btn btn-success" onClick={handleConfirmarSimples} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Processando...
                                        </>
                                    ) : (
                                        'Confirmar realização'
                                    )}
                                </button>
                            )}
                        </Modal.Footer>
                    </form>
                ) : (
                    <>
                        <Modal.Header closeButton>
                            <span className="fw-semibold fs-5 text-primary">Marcar como Realizado</span>
                        </Modal.Header>
                        <Modal.Body className="bg-gray-100 p-4">
                            {resumoAgendamento}
                        </Modal.Body>
                        <Modal.Footer className="justify-content-end">
                            <button type="button" className="btn me-2" onClick={onHide} disabled={loading}>
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-success" onClick={handleConfirmarSimples} disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Processando...
                                    </>
                                ) : (
                                    'Confirmar'
                                )}
                            </button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>

            <Modal show={showAlerta} onHide={() => setShowAlerta(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="text-danger">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Erro
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-danger mb-0" role="alert">
                        {mensagemAlerta}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button type="button" className="btn btn-primary" onClick={() => setShowAlerta(false)}>
                        Entendi
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default ModalRealizarAgendamento;
